import { FC, useEffect, useRef } from 'react';
import { useIntl } from '../../../../../../_metronic/i18n/customUseIntl';
import { useFormContext } from 'react-hook-form';
import { cloneDeep } from 'lodash';
import { DialogContainer } from '../../CommonDataParts/DialogContainer2';
import DsmButtonControlGroup from '../../../helpers/DsmButtonControlGroup';
import { BaselineDialogProps } from "../../common";
import { FacilityTableItem } from '../../../../models/Facility/FacilityTypes';
import {
    ShrimpBaseline,
    ShrimpProcessingOutput,
    ShrimpProcessingPackaging,
    ShrimpProcessingStageData
} from '../../../../models/Baseline/ShrimpBaseline';
import ShrimpPackagingForm from './ShrimpPackagingForm';

interface ShrimpPackagingDialogProps
    extends BaselineDialogProps {
    facility: FacilityTableItem | undefined;
}

const ShrimpPackagingDialog: FC<ShrimpPackagingDialogProps> = ({
    formType,
    itemIndex = 0,
    formVisible,
    handleCancel,
    facility
}) => {
    const intl = useIntl();
    const fc = useFormContext<ShrimpBaseline>();
    const fieldItemPrefix = `stages.${itemIndex}.stageData.processingPackaging`;
    const currResetValue = useRef<ShrimpProcessingPackaging>();

    useEffect(() => {
        if (formVisible) {
            currResetValue.current = cloneDeep<ShrimpProcessingPackaging>(
                fc.getValues(fieldItemPrefix)
            );
        }
    }, [formVisible]);

    const handleCloseDialog = () => {
        if (currResetValue.current) {
            const resetObject = { ...fc.getValues() };
            const stageData = resetObject.stages[itemIndex]
                ?.stageData as ShrimpProcessingStageData;
            if (stageData?.processingPackaging) {
                stageData.processingPackaging = currResetValue.current;
                fc.reset(resetObject, { errors: true });
            }
        }

        if (handleCancel) handleCancel();
    };

    const outputsData = fc.getValues(`stages.${itemIndex}.stageData.processingOutput`) as ShrimpProcessingOutput;

    const filterOutputs = (data?: ShrimpProcessingOutput): ShrimpProcessingOutput | undefined => {
        if (!data) return undefined;

        return {
            freshMeatAndEdibleOffal: data.freshMeatAndEdibleOffal?.filter(o => o.outputName !== "" && o.outputName !== undefined) || [],
            cat3SlaughterByProducts: data.cat3SlaughterByProducts?.filter(o => o.outputName !== "" && o.outputName !== undefined) || [],
            cat1And2MaterialAndWaste: data.cat1And2MaterialAndWaste?.filter(o => o.outputName !== "" && o.outputName !== undefined) || [],
        };
    };

    return (
        <DialogContainer
            formVisible={formVisible}
            handleClose={handleCloseDialog}
            iconCode="shapes/cube-02"
            variant="wide"
            formTitle={intl.formatMessage({ id: 'SUSTELL.STAGES.PROCESSING.PACKAGING.TITLE' })}
            introText={intl.formatMessage({ id: 'SUSTELL.STAGES.PROCESSING.PACKAGING.SHRIMP.DESCRIPTION' })}
        >
            <ShrimpPackagingForm
                formType={formType}
                fieldItemPrefix={fieldItemPrefix}
                baseline={outputsData ? filterOutputs(outputsData) : undefined}
            />
            <DsmButtonControlGroup
                cancelHandler={handleCloseDialog}
                saveLabel={intl.formatMessage({ id: 'GENERAL.CONFIRM' })}
                saveHandler={async () => {
                    await fc.trigger(
                        `stages.${itemIndex}.stageData.processingPackaging`
                    );
                    handleCancel("confirm");
                }}
            />
        </DialogContainer>
    );
};

export default ShrimpPackagingDialog;
